import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import {
  FloorplanFreeTextListType,
  FREE_TEXT_DEFAULT_FONT_SIZE,
  FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
  FREE_TEXT_MAX_TEXT_BOX_WIDTH,
  FREE_TEXT_MIN_TEXT_BOX_HEIGHT,
  FREE_TEXT_MIN_TEXT_BOX_WIDTH,
} from "@lib/constants/floorplan-free-text";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";

export class FreeTextKonva {
  private static readonly PADDING = 4;

  static init(freeText: FloorplanFreeText): Konva.Group {
    const parentGroup = new Konva.Group();

    const group = new Konva.Group({
      name: "freeText",
      x: freeText.x,
      y: freeText.y,
      draggable: false,
    });
    const text = new Konva.Text({
      name: "freeTextValue",
      text: this.formatListText(freeText),
      fontSize: freeText.fontSize ?? FREE_TEXT_DEFAULT_FONT_SIZE,
      fill: freeText.textColor ?? "#002766",
      fontStyle: this.getFontStyle(freeText),
      padding: this.PADDING,
      lineHeight: 1.2,
      wrap: "word",
    });
    const background = new Konva.Rect({
      name: "freeTextBackground",
      fill: "#FFFFFF",
      opacity: 0.8,
      cornerRadius: 3,
    });
    const originalGetClientRect = group.getClientRect.bind(group);
    group.getClientRect = (config) => {
      if (group.getAttr("freeTextTransformActive")) {
        return background.getClientRect(config);
      }

      return originalGetClientRect(config);
    };
    const itemText = FloorplanItemTextKonva.init(freeText, freeText.fontSize);

    group.add(background, text);
    parentGroup.add(group, itemText);
    this.updateTextLayout(freeText, group, text);
    this.scaleItemText(group, itemText);
    this.positionItemText(group, itemText, freeText);
    group.on("dragmove", () => this.positionItemText(group, itemText, freeText));
    group.on("transform", () => {
      if (group.getAttr("freeTextTransformActive")) {
        return;
      }

      this.scaleItemText(group, itemText);
      this.positionItemText(group, itemText, freeText);
    });
    return parentGroup;
  }

  static refreshText(freeText: FloorplanFreeText, parentGroup: Konva.Group): void {
    const group = parentGroup.findOne(".freeText") as Konva.Group | null;
    const text = group?.findOne(".freeTextValue") as Konva.Text | null;
    if (!group || !text) {
      return;
    }

    text.text(this.formatListText(freeText));
    text.fill(freeText.textColor);
    text.fontSize(freeText.fontSize ?? FREE_TEXT_DEFAULT_FONT_SIZE);
    text.fontStyle(this.getFontStyle(freeText));
    text.wrap("word");
    text.setAttr("height", undefined);
    this.updateTextLayout(freeText, group, text);

    const itemText = parentGroup.findOne(".itemText") as Konva.Group | null;
    if (itemText) {
      FloorplanItemTextKonva.refreshText(freeText, itemText);
    }
  }

  static repositionItemText(freeText: FloorplanFreeText, parentGroup: Konva.Group): void {
    const group = parentGroup.findOne(".freeText") as Konva.Group | null;
    const itemText = parentGroup.findOne(".itemText") as Konva.Group | null;
    if (!group || !itemText) {
      return;
    }

    this.positionItemText(group, itemText, freeText);
  }

  static applyItemTextScale(freeText: FloorplanFreeText, parentGroup: Konva.Group, scaleX: number, scaleY: number): void {
    const group = parentGroup.findOne(".freeText") as Konva.Group | null;
    const itemText = parentGroup.findOne(".itemText") as Konva.Group | null;
    if (!group || !itemText) {
      return;
    }

    itemText.scale({ x: scaleX, y: scaleY });
    FloorplanItemTextKonva.updateBackground(itemText);
    this.positionItemText(group, itemText, freeText);
  }

  private static scaleItemText(group: Konva.Group, itemText: Konva.Group): void {
    FloorplanItemTextKonva.scaleTextGroup(itemText, group);
    FloorplanItemTextKonva.updateBackground(itemText);
  }

  private static positionItemText(group: Konva.Group, itemText: Konva.Group, freeText: FloorplanFreeText): void {
    LabelLayoutKonva.placeLabelForItem(itemText, group, freeText);
  }

  private static getFontStyle(freeText: FloorplanFreeText): string {
    if (freeText.bold && freeText.italic) {
      return "bold italic";
    }

    if (freeText.bold) {
      return "bold";
    }

    if (freeText.italic) {
      return "italic";
    }

    return "normal";
  }

  private static formatListText(freeText: FloorplanFreeText): string {
    if (freeText.listType === FloorplanFreeTextListType.NONE) {
      return freeText.content;
    }

    let itemNumber = 0;
    return freeText.content
      .split("\n")
      .map((line) => {
        const content = line.trimStart();
        if (content.length === 0) {
          return "";
        }

        if (freeText.listType === FloorplanFreeTextListType.BULLET) {
          return `• ${content}`;
        }

        itemNumber += 1;
        return `${itemNumber}. ${content}`;
      })
      .join("\n");
  }

  static getTextBoxWidth(freeText: FloorplanFreeText, text: Konva.Text): number {
    if (freeText.textBoxWidth !== undefined) {
      return freeText.textBoxWidth;
    }

    return Math.min(text.getTextWidth() + 2 * this.PADDING, FREE_TEXT_MAX_TEXT_BOX_WIDTH);
  }

  private static updateTextLayout(freeText: FloorplanFreeText, group: Konva.Group, text: Konva.Text, width?: number): void {
    text.width(Math.max(FREE_TEXT_MIN_TEXT_BOX_WIDTH, width ?? this.getTextBoxWidth(freeText, text)));

    if (freeText.textBoxHeight !== undefined) {
      text.height(Math.min(FREE_TEXT_MAX_TEXT_BOX_HEIGHT, Math.max(FREE_TEXT_MIN_TEXT_BOX_HEIGHT, freeText.textBoxHeight)));
    } else {
      text.setAttr("height", undefined);
    }

    this.updateBackground(group);
    group.setAttr("width", text.width());
    group.setAttr("height", text.height());
  }

  private static updateBackground(group: Konva.Group): void {
    const background = group.findOne(".freeTextBackground") as Konva.Rect | null;
    const text = group.findOne(".freeTextValue") as Konva.Text | null;
    if (!background || !text) {
      return;
    }

    background.width(text.width());
    background.height(text.height());
  }
}
